/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Fadeable from '../gfx/Fadeable';

import vertexShader from '../shaders/ARModelPlacer/ARModelPlacer.vert';
import fragmentShader from '../shaders/ARModelPlacer/ARModelPlacer.frag';

let tempMatrix = new THREE.Matrix4();
let tempPos = new THREE.Vector3();
let tempQuat = new THREE.Quaternion();
let tempScale = new THREE.Vector3();

let tempCameraPos = new THREE.Vector3();
let tempCameraDir = new THREE.Vector3();
let tempCameraQuat = new THREE.Quaternion();

let tempCastPos = new THREE.Vector2(0, 0);

export default class ARModelPlacer extends Fadeable {
  constructor(props) {
    super(props);
    this.setup(props);
  }

  setup = props => {
    this.setupProperties(props);
    this.setupPlacer(props);
    this.setupFloor(props);
  };

  setupProperties = props => {
    this.props = props;
    this.offsetAngle = props.offsetAngle != undefined ? props.offsetAngle : 0.0;
    this.size = new THREE.Vector3();
    this.bb = props.boundingBox;
    this.bb.getSize(this.size);

    this.renderer = props.renderer;
    this.alpha.value = 1.0;
    this.visible = true;

    this.tracking = { value: 0.0 };
    this.trackingChangeTransitionSpeed = 0.1;
    this.easing = this.props.easing;

    this.trackingState = false;
    this.previousTrackingState = false;

    this.xrSession = this.props.xrSession;
    this.frameOfRef = this.props.frameOfRef;

    this.camera = this.props.camera;
    this.raycaster = new THREE.Raycaster();
    this.fallbackRaycaster = new THREE.Raycaster();

    if (this.xrSession) {
      this.planeDir = new THREE.Vector3();
      this.startTime = Date.now();
    }
  };

  setupPlacer = props => {
    this.setupMaterial(props);
    this.setupGeometry(props);
    this.setupMesh();
  };

  setupMaterial = props => {
    this.material = new THREE.RawShaderMaterial({
      uniforms: {
        time: {
          value: 0.0,
        },
        tracking: {
          value: 0.0,
        },
        speed: {
          value: props.speed != undefined ? props.speed : 5.0,
        },
        thickness: {
          value: props.padding != undefined ? props.padding : 0.1,
        },
        radius: {
          value: props.radius != undefined ? props.radius : 0.1,
        },
        aspect: {
          value: this.size.x / this.size.z,
        },
        color: {
          value: props.color != undefined
            ? props.color
            : new THREE.Color(0xffffff),
        },
        alpha: this.alpha,
        opacity: {
          value: props.opacity != undefined ? props.opacity : 1.0,
        },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  };

  setupGeometry = props => {
    this.geometry = new THREE.PlaneBufferGeometry(
      this.size.x + props.padding,
      this.size.z + props.padding
    );
    this.geometry.applyMatrix(
      new THREE.Matrix4().makeRotationX(THREE.Math.degToRad(-90))
    );
  };

  setupMesh = () => {
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.renderOrder = -5;
    this.add(this.mesh);
    this.mesh.onBeforeRender = this.updateUniforms;
  };

  setupFloor = () => {
    let geo = new THREE.PlaneBufferGeometry(10, 10, 10, 10);
    geo.applyMatrix(
      new THREE.Matrix4().makeRotationX(THREE.Math.degToRad(-90))
    );
    this.floor = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        color: 0xff0000,
        opacity: 0.0,
        wireframe: true,
        depthWrite: false,
        transparent: true,
      })
    );
    this.floor.visible = false;
    this.floor.renderOrder = -10;
    this.add(this.floor);
  };

  async update() {
    if (!this.camera || !this.xrSession || !this.xrSession.requestHitTest) {
      return;
    }

    this.raycaster.setFromCamera({ x: 0.0, y: 0.0 }, this.camera);
    const ray = this.raycaster.ray;

    const origin = new Float32Array(ray.origin.toArray());
    const direction = new Float32Array(ray.direction.toArray());

    const hit = await this.xrSession.requestHitTest(
      origin,
      direction,
      this.frameOfRef
    );

    this.camera.getWorldPosition(tempCameraPos);
    if (hit && hit.length > 0) {
      tempMatrix.fromArray(hit[0].hitMatrix);
      tempMatrix.decompose(tempPos, tempQuat, tempScale);
      this.mesh.position.lerp(tempPos, this.easing);
      let x = tempCameraPos.x - this.mesh.position.x;
      let z = tempCameraPos.z - this.mesh.position.z;
      let angle = Math.atan2(x, z);
      this.mesh.rotation.set(0, angle + this.offsetAngle, 0);
      this.mesh.updateMatrixWorld(true);
      this.setTrackingState(true);
      this.floor.position.copy(tempPos);
      this.floor.updateMatrixWorld(true);
      this.floor.visible = true;
    } else {
      // raycast onto the floor
      if (this.floor.visible) {
        this.fallbackRaycaster.setFromCamera(tempCastPos, this.camera);
        let result = this.fallbackRaycaster.intersectObjects(
          [this.floor],
          false
        );
        if (result && result[0]) {
          this.mesh.position.lerp(result[0].point, this.easing);
          this.mesh.updateMatrixWorld(true);
        }
      }
      this.setTrackingState(false);
    }

    let scale = THREE.Math.mapLinear(this.alpha.value, 0.0, 1.0, 1.1, 1.0);
    this.mesh.scale.set(scale, scale, scale);
  }

  updateUniforms = () => {
    let elapsedMilliseconds = Date.now() - this.startTime;
    let elapsedSeconds = elapsedMilliseconds / 1000;
    this.material.uniforms.time.value = elapsedSeconds;
    this.material.uniforms.tracking.value = this.tracking.value;
  };

  setTrackingState = state => {
    this.trackingState = state;
    let speed = this.trackingChangeTransitionSpeed;
    this.tracking.value += state ? speed : -speed;
    this.tracking.value = Math.max(Math.min(this.tracking.value, 1.0), 0.0);
    this.updateTrackingState();
  };

  updateTrackingState = () => {
    if (this.trackingState !== this.previousTrackingState) {
      if (this.trackingState) {
        this.dispatchEvent({ type: 'foundSurface', object: this });
      } else {
        this.dispatchEvent({ type: 'findingSurface', object: this });
      }
    }
    this.previousTrackingState = this.trackingState;
  };

  getTrackingState = () => {
    return this.trackingState;
  };

  getPosition = () => {
    return this.mesh.position;
  };
}



// WEBPACK FOOTER //
// ./src/ar/ARModelPlacer.js